import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type UserPublic } from '@forest/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Info } from 'lucide-react';
import { api, apiError } from '@/lib/api-client';
import {
  applyServerFieldErrors,
  makeRegisterFormSchema,
  type RegisterFormValues,
} from '@/lib/user-validation';
import { useAuthStore } from '@/stores/sessionStore';
import { useUiStore } from '@/stores/uiStore';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { PasswordRules } from '@/components/PasswordRules';

export function RegisterPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  const pushToast = useUiStore((s) => s.pushToast);
  const qc = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) navigate('/app/parcels', { replace: true });
  }, [user, navigate]);

  // Rebuilt on language change so field errors re-localize on the next
  // validation pass.
  const resolver = useMemo(() => zodResolver(makeRegisterFormSchema(t)), [t]);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    getValues,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver,
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
      name: '',
      preferredLanguage: 'ro',
    },
  });

  const passwordValue = watch('password');
  const confirmValue = watch('confirmPassword');

  const mut = useMutation({
    mutationFn: (data: RegisterFormValues) => {
      const { confirmPassword: _, ...payload } = data;
      void _;
      return api.post('auth/sign-up/username', { json: payload }).json<{ user: UserPublic }>();
    },
    onSuccess: (data) => {
      setUser(data.user);
      void qc.invalidateQueries({ queryKey: ['auth', 'session'] });
      navigate('/app/parcels', { replace: true });
    },
    onError: async (e) => {
      const err = await apiError(e);
      const handled = applyServerFieldErrors({
        err,
        setError,
        t,
        fields: ['name', 'username', 'password', 'preferredLanguage'] as const,
        getValue: (f) => getValues(f as 'password'),
      });
      if (handled) return;
      pushToast({
        variant: 'error',
        message:
          err.code === 'VALIDATION_FAILED'
            ? t('common:validation.checkFields')
            : err.message || t('register.error'),
      });
    },
  });

  const toggleIcon = showPassword ? (
    <EyeOff className="h-4 w-4 text-slate-400 dark:text-slate-500" />
  ) : (
    <Eye className="h-4 w-4 text-slate-400 dark:text-slate-500" />
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-slate-900">
      <Card className="w-full max-w-md">
        <CardBody>
          <h1 className="mb-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">{t('register.title')}</h1>
          <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">{t('register.subtitle')}</p>
          <div
            role="note"
            className="mb-4 flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
          >
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
            <div>
              <p className="font-semibold">{t('register.adminAssignmentNotice.title')}</p>
              <p className="mt-0.5 leading-snug">
                {t('register.adminAssignmentNotice.body')}
              </p>
            </div>
          </div>
          <form onSubmit={handleSubmit((d) => mut.mutate(d))} noValidate>
            <FormField
              label={t('register.fullName')}
              htmlFor="name"
              error={errors.name?.message}
              hint={t('common:validation.name.hint')}
              required
            >
              <Input id="name" autoComplete="name" {...register('name')} />
            </FormField>
            <FormField
              label={t('register.username')}
              htmlFor="username"
              error={errors.username?.message}
              hint={t('common:validation.username.hint')}
              required
            >
              <Input id="username" autoComplete="username" {...register('username')} />
            </FormField>
            <FormField
              label={t('register.password')}
              htmlFor="password"
              error={errors.password?.message}
              required
            >
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? t('register.hidePassword') : t('register.showPassword')}
                >
                  {toggleIcon}
                </button>
              </div>
              <PasswordRules value={passwordValue ?? ''} />
            </FormField>
            <FormField
              label={t('register.confirmPassword')}
              htmlFor="confirmPassword"
              error={errors.confirmPassword?.message}
              required
            >
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="pr-10"
                  {...register('confirmPassword')}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? t('register.hidePassword') : t('register.showPassword')}
                >
                  {toggleIcon}
                </button>
              </div>
              {passwordValue && confirmValue && passwordValue !== confirmValue && !errors.confirmPassword && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{t('register.passwordMismatch')}</p>
              )}
            </FormField>
            <FormField label={t('register.language')} htmlFor="lang">
              <Select id="lang" {...register('preferredLanguage')}>
                <option value="ro">Română</option>
                <option value="ru">Русский</option>
                <option value="en">English</option>
              </Select>
            </FormField>
            <Button type="submit" className="mt-2 w-full" loading={mut.isPending}>
              {t('register.submit')}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-slate-600 dark:text-slate-400">
            {t('register.haveAccount')}{' '}
            <Link to="/login" className="font-medium text-forest-700 hover:underline dark:text-forest-300">
              {t('register.login')}
            </Link>
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
